import _slicedToArray from "C:\\a\\2\\s\\Programs\\Microservices.Common.Frontend\\ecrion-components\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/slicedToArray"; //@ts-check

import React, { useContext, useEffect } from 'react';
import { RunContext } from './RunContext';
import apiClient from '../data';
import Utils from '../../../../utils';
import Modals from '../../../../modal';
var PreviewMode = 'PreviewMode';
/**
 * @param {import('utils/apiClients/types').Project} project
 * @param {import('../data/types').JobSummary} job
 * @param {import('./types').RunOptions} options
 * @param {Array<{name:string, value:string}>} parameters
 * @param {React.RefObject<import('../../EcrionForms/api/types').EcrionFormApi>} formApiRef
 * @returns {import('./types').useRunAPIResult}
 */

var useRun = function useRun(project, job, options, parameters, formApiRef) {
  var requests = React.useRef({
    processInputRequest: null
  }); // keep the ref in order to cancel the previous request

  var _useContext = useContext(RunContext),
      _useContext2 = _slicedToArray(_useContext, 3),
      runState = _useContext2[0],
      runDispatch = _useContext2[1],
      documentRef = _useContext2[2];

  var Workspace = project.Workspace,
      WorkflowPath = project.WorkflowPath,
      FormPath = project.FormPath;
  var ShowVersionsButton = project.ShowVersionsButton,
      EnableDiscussion = project.EnableDiscussion;
  var AutosaveInterval = project.AutosaveInterval,
      PreviewRefreshDelay = project.PreviewRefreshDelay;
  var ManualPreviewRefresh = project.ManualPreviewRefresh;
  var DocumentView = project.DocumentView,
      ShowFormView = project.ShowFormView;
  var Name = project.Name,
      ShowAdvancedMenu = project.ShowAdvancedMenu,
      Version = project.Version;

  var _ref = job || {},
      InputPath = _ref.InputPath,
      Id = _ref.Id;

  var enableAdvanced = options.enableAdvanced,
      workItemPath = options.workItemPath,
      workItemWorkspace = options.workItemWorkspace;

  var highlightField = function highlightField(field) {
    runDispatch({
      type: 'HIGHLIGHT_FIELD',
      payload: field
    });
  };

  var onFieldClicked = function onFieldClicked(field) {
    if (!FormPath) return; // search for the field in form using a custom logic
    // we keep this logic here since it is a 'run communication' logic, not a form one
    // search field by type

    var fields = formApiRef.current.searchFields({
      type: field.type,
      matchProperties: {
        name: field.label
      },
      inputFieldsOnly: true
    });
    if (!fields || !fields.length) return;
    formApiRef.current.editField(fields[0]);
  };

  var onStateChanged = function onStateChanged(message, payload) {
    message === 'refreshing' && runDispatch({
      type: 'SET_REFRESHING_DATA',
      payload: {
        refreshing: payload
      }
    });
    message === 'error' && runDispatch({
      type: 'STATE_CHANGED',
      payload: {
        error: Utils.getPrettyError(payload),
        loaded: true
      }
    });
  };

  var selectTemplate = function selectTemplate(template) {
    runDispatch({
      type: 'SELECT_TEMPLATE',
      payload: template
    });
  };

  var setZoomTitle = function setZoomTitle(zoom) {
    runDispatch({
      type: 'SET_ZOOM_TITLE',
      payload: zoom
    });
  };

  var toggleDiscussion = function toggleDiscussion(visible) {
    runDispatch({
      type: 'SET_DISCUSSION_VISIBLE',
      payload: visible
    });
  };

  var toggleViews = function toggleViews() {
    var _runState$view = runState.view,
        hideFormView = _runState$view.hideFormView,
        hideDocumentView = _runState$view.hideDocumentView;
    if (!hideFormView && !hideDocumentView) hideDocumentView = true;else if (hideFormView) hideFormView = false;else {
      hideFormView = !hideFormView;
      hideDocumentView = !hideDocumentView;
    }
    runDispatch({
      type: 'SET_VIEW',
      payload: {
        hideFormView: hideFormView,
        hideDocumentView: hideDocumentView
      }
    });
  };

  var openProjectPage = function openProjectPage() {
    var host = Utils.getHost() || '/';
    var url = host + 'EOS/Projects' + '#projects/Projects/' + encodeURIComponent(Name);
    window.open(url, '_blank', '');
  };

  var downloadInput = function downloadInput(version) {
    var host = Utils.getHost() || '/';
    var path = encodeURIComponent(InputPath);
    var workspace = encodeURIComponent(Workspace);
    var fileName = encodeURIComponent(job.JobName + '.xml');
    Utils.request({
      url: '/backend/fileresourcetoken',
      query: {
        workspace: Workspace,
        filePath: InputPath
      }
    }).then(function (resourceToken) {
      var apiUrl = "".concat(host, "backend/content?resourceToken=").concat(resourceToken, "&path=").concat(path, "&workspace=").concat(workspace, "&fileName=").concat(fileName, "&version=").concat(version);
      window.open(apiUrl, '_blank', '');
    });
  };

  var restoreVersion = function restoreVersion(version) {
    runDispatch({
      type: 'SET_CURRENT_DATA_VERSION',
      payload: -1
    });
    Utils.apiClients.content.getContent(Workspace, InputPath, false, version).then(function (data) {
      runDispatch({
        type: 'SET_FORM_DATA',
        payload: {
          data: data,
          readonly: false
        }
      });
    });
  };

  var uploadVersion = function uploadVersion() {
    Modals.open({
      type: 'UPLOAD_VERSION_DIALOG',
      props: {
        onSubmit: function onSubmit() {
          Utils.apiClients.forms.getFormVersions(Workspace, InputPath).then(function (versions) {
            var crtVersion = {
              Version: -1
            };
            var enhancedVersions = [crtVersion].concat(versions);
            runDispatch({
              type: 'SET_DATA_VERSIONS',
              payload: enhancedVersions
            });
          });
          Utils.apiClients.content.getContent(Workspace, InputPath, false, -1).then(function (data) {
            runDispatch({
              type: 'SET_FORM_DATA',
              payload: {
                data: data,
                readonly: false
              }
            });
          });
        },
        workspace: Workspace,
        filePath: InputPath
      }
    });
  };

  var renameJob = function renameJob() {
    Modals.open({
      type: Modals.TYPES.PROMPT_INPUT,
      props: {
        windowProps: {
          header: 'Rename Document'
        },
        formFieldType: 'Name',
        formField: {
          selectOnFocus: true,
          defaultValue: '',
          label: 'Name',
          placeholder: 'Name of the document'
        },
        submitLabel: 'Rename',
        submittingLabel: 'Renaming..',
        onSubmit: function onSubmit(newName) {
          return Utils.apiClients.jobs.renameJob(Workspace, Id, newName).then(function (jobSummary) {
            options.onJobUpdated && options.onJobUpdated(jobSummary);
          });
        }
      }
    });
  };

  var deleteJob = function deleteJob() {
    Modals.open({
      type: Modals.TYPES.DELETE_CONFIRMATION,
      props: {
        title: 'Delete?',
        message: "Are you sure you want to delete ".concat(job.JobName, " ?"),
        onDelete: function onDelete() {
          return Utils.apiClients.jobs.deleteJob(Workspace, Id).then(function () {
            options.onJobDeleted && options.onJobDeleted();
          });
        }
      }
    });
  };

  var selectFormVersion = function selectFormVersion(version) {
    runDispatch({
      type: 'SET_CURRENT_DATA_VERSION',
      payload: version
    });
    Utils.apiClients.content.getContent(Workspace, InputPath, false, version).then(function (data) {
      runDispatch({
        type: 'SET_FORM_DATA',
        payload: {
          data: data,
          readonly: version !== -1
        }
      });
      runDispatch({
        type: 'SET_DISCUSSION_SOURCE',
        payload: {
          path: InputPath,
          workspace: Workspace,
          version: version
        }
      });
    });
  };

  var setFormData = function setFormData(formXml) {
    var jobParameters = parameters.map(function (p) {
      return {
        Name: p.name,
        Value: p.value
      };
    });
    var previewModeParameter = jobParameters.find(function (p) {
      return p.Name && p.Name.toLowerCase() === PreviewMode.toLowerCase();
    });
    if (!previewModeParameter) jobParameters.push({
      Name: PreviewMode,
      Value: 'true'
    });
    if (requests.current.processInputRequest) // cancel previous process input request
      requests.current.processInputRequest.cancel();
    requests.current.processInputRequest = Utils.makeCancelable(apiClient.processInput(Workspace, WorkflowPath, formXml, jobParameters, Id, Version));
    runDispatch({
      type: 'SET_PROCESSING_DATA',
      payload: {
        processing: true
      }
    });
    requests.current.processInputRequest.promise.then(function (formData) {
      apiClient.getTemplates(Workspace, WorkflowPath, formData, jobParameters).then(function (data) {
        runDispatch({
          type: 'SET_TEMPLATE_INPUT',
          payload: formData
        });
        runDispatch({
          type: 'SET_TEMPLATES',
          payload: {
            templates: data.Templates,
            templatesFieldsMap: data.TemplatesFieldsMap
          }
        });
        runDispatch({
          type: 'STATE_CHANGED',
          payload: {
            error: '',
            loaded: true
          }
        });
        runDispatch({
          type: 'SET_PROCESSING_DATA',
          payload: {
            processing: false
          }
        });
      });
    }).catch(function (e) {
      if (e.isCanceled) return;
      runDispatch({
        type: 'SET_PROCESSING_DATA',
        payload: {
          processing: false
        }
      });
      runDispatch({
        type: 'STATE_CHANGED',
        payload: {
          error: Utils.getPrettyError(e),
          loaded: true
        }
      });
    });
  };

  var setZoom = function setZoom(command) {
    if (!documentRef || !documentRef.current) return;
    var documentRefComponent = documentRef.current.getWrappedInstance();
    if (!documentRefComponent) return;
    documentRefComponent.changeZoom(command);
  };
  /** Initial loading of data */


  useEffect(function () {
    if (EnableDiscussion) runDispatch({
      type: 'SET_DISCUSSION',
      payload: {
        enabled: true,
        visible: true,
        source: {
          path: InputPath,
          workspace: Workspace,
          version: -1
        }
      }
    });
    var view = {
      hasFormView: !!FormPath,
      hasDocumentView: DocumentView || !FormPath,
      hideFormView: !ShowFormView
    };
    view.allowViewSwitch = ShowFormView && FormPath && view.hasDocumentView;
    runDispatch({
      type: 'SET_VIEW',
      payload: view
    });
  }, []);
  useEffect(function () {
    if (!DocumentView && FormPath) return; // when no FormData, we run get templates with no data

    if (!FormPath) return setFormData('');
  }, [DocumentView, FormPath]);
  useEffect(function () {
    // in case of 'on-demand' there is no form path
    if (!FormPath) return;
    Utils.request({
      url: '/backend/environmentsettings'
    }).then(function (res) {
      var formOptions = res[0].FormsSettings;
      var emailBlacklist = formOptions.EmailBlacklist;
      var googleAPIKey = formOptions.GoogleApiKey;
      var options = {
        workspace: Workspace,
        path: FormPath,
        jobId: Id || '',
        emailBlacklist: emailBlacklist,
        googleAPIKey: googleAPIKey
      };
      var dataPath = InputPath || workItemPath;
      var dataWorkspace = InputPath ? Workspace : workItemWorkspace || Workspace;
      Utils.apiClients.forms.getFormContent(Workspace, FormPath).then(function (content) {
        // we can fetch form form data either from
        if (!dataPath) runDispatch({
          type: 'SET_FORM_DATA',
          payload: {
            content: content,
            data: '',
            options: options
          }
        });else Utils.apiClients.content.getContent(dataWorkspace, dataPath).then(function (data) {
          runDispatch({
            type: 'SET_FORM_DATA',
            payload: {
              content: content,
              data: data,
              options: options
            }
          });
        });
      });
    });
  }, [Workspace, InputPath, FormPath]);
  useEffect(function () {
    if (!enableAdvanced && !ShowVersionsButton || !InputPath) return;
    Utils.apiClients.forms.getFormVersions(Workspace, InputPath).then(function (versions) {
      var crtVersion = {
        Version: -1
      };
      var enhancedVersions = [crtVersion].concat(versions);
      runDispatch({
        type: 'SET_DATA_VERSIONS',
        payload: enhancedVersions
      });
      runDispatch({
        type: 'SET_DISCUSSION_SOURCE',
        payload: {
          path: InputPath,
          workspace: Workspace,
          version: -1
        }
      });
    });
  }, [Workspace, InputPath]);
  useEffect(function () {
    if (!documentRef || !documentRef.current) return;
    var documentRefComponent = documentRef.current.getWrappedInstance();
    if (!documentRefComponent) return;
    var hideDocumentView = runState.view.hideDocumentView; // small hack to force a refresh when changing the view

    if (!hideDocumentView) {
      documentRefComponent.changeZoom('zoomIn');
      documentRefComponent.changeZoom('zoomOut');
    }
  }, [runState.view.hideFormView, runState.view.hideDocumentView]);

  var refreshCurrentVersions = function refreshCurrentVersions() {
    if (!enableAdvanced && !ShowVersionsButton || !InputPath) return;
    Utils.apiClients.forms.getFormVersions(Workspace, InputPath).then(function (versions) {
      var crtVersion = {
        Version: -1
      };
      var enhancedVersions = [crtVersion].concat(versions);
      runDispatch({
        type: 'SET_DATA_VERSIONS',
        payload: enhancedVersions
      });
    });
  };

  var saveCurrentFormData = function saveCurrentFormData(trackingData) {
    runDispatch({
      type: 'SET_SAVING',
      payload: true
    });
    return formApiRef.current.getData(trackingData.current).then(function (formXml) {
      return apiClient.setJobInput(Workspace, Id, formXml).then(function () {
        formApiRef.current.setPristine();
        runDispatch({
          type: 'SET_SAVING',
          payload: false
        });
        refreshCurrentVersions();
      });
    });
  };

  return {
    state: runState,
    documentRef: documentRef,
    actions: {
      highlightField: highlightField,
      onStateChanged: onStateChanged,
      onFieldClicked: onFieldClicked,
      selectTemplate: selectTemplate,
      setZoomTitle: setZoomTitle,
      setZoom: setZoom,
      selectFormVersion: selectFormVersion,
      setFormData: setFormData,
      toggleViews: toggleViews,
      toggleDiscussion: toggleDiscussion,
      downloadInput: downloadInput,
      restoreVersion: restoreVersion,
      uploadVersion: uploadVersion,
      renameJob: renameJob,
      deleteJob: deleteJob,
      openProjectPage: openProjectPage,
      saveCurrentFormData: saveCurrentFormData
    },
    options: {
      ShowAdvancedMenu: ShowAdvancedMenu,
      JobId: Id,
      AutosaveInterval: AutosaveInterval,
      PreviewRefreshDelay: PreviewRefreshDelay,
      ManualPreviewRefresh: ManualPreviewRefresh
    }
  };
};

export default useRun;